import { createFileRoute, Link } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Sparkles, Globe, Heart, Shield, Eye, Zap } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import heroAsset from "@/assets/about/hero.webp.asset.json";
import storyAsset from "@/assets/about/story.webp.asset.json";

const heroImg = heroAsset.url;
const storyImg = storyAsset.url;
const heroAlt =
  "Χαμογελαστός πελάτης της Banana με καλοραμμένο σακάκι κρατά μια μοβ επαγγελματική κάρτα NFC σε σύγχρονο γραφείο ουρανοξύστη με θέα στην πόλη";
const storyAlt =
  "Σύγκριση μιας φθαρμένης χάρτινης επαγγελματικής κάρτας Six Studio με την ίδια επαφή ως ψηφιακό προφίλ Banana σε iPhone, δίπλα σε μια μοβ έξυπνη κάρτα NFC";

export const Route = createFileRoute("/el/about")({
  head: () => ({
    meta: [
      { title: "Σχετικά με εμάς — banana.cards" },
      {
        name: "description",
        content:
          "Η Banana είναι η πλατφόρμα επαφών με AI που βοηθά περισσότερους από 200.000 επαγγελματίες να μετατρέπουν τις συζητήσεις σε σχέσεις που διαρκούν. Γνωρίστε την ομάδα και την ιστορία μας.",
      },
      { property: "og:title", content: "Σχετικά με το banana.cards" },
      {
        property: "og:description",
        content: "Η ιστορία μας, η αποστολή μας και οι άνθρωποι πίσω από το banana.cards.",
      },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/about", "el"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "Σχετικά με το banana.cards",
          description: "Η Banana είναι η πλατφόρμα επαφών με AI που βοηθά περισσότερους από 200.000 επαγγελματίες να μετατρέπουν τις συζητήσεις σε σχέσεις που διαρκούν.",
          url: "https://banana.cards/el/about",
          mainEntity: {
            "@type": "Organization",
            name: "Banana Cards",
            url: "https://banana.cards/el",
            foundingDate: "2022",
            parentOrganization: { "@type": "Organization", name: "SIX STUDIO CREATIVE - FZCO" },
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Σχετικά με εμάς</p>
            <h1 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
              Βοηθάμε τους ανθρώπους <em className="italic">να θυμούνται ανθρώπους.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Η Banana ξεκίνησε με μια απλή ιδέα: η δικτύωση δεν πρέπει να τελειώνει στο «χάρηκα για τη γνωριμία». Δημιουργούμε
              την πλατφόρμα με AI που μετατρέπει τις συζητήσεις σε σχέσεις που διαρκούν — και τις σχέσεις σε
              ουσιαστική ανάπτυξη, συμφωνίες και φιλίες.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]"
              >
                Δοκιμάστε την Banana δωρεάν <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link
                to="/el/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70"
              >
                Μιλήστε με την ομάδα
              </Link>
            </div>
          </div>
          <img src={heroImg} alt={heroAlt} className="w-full rounded-2xl object-cover" />
        </div>
      </section>

      {/* STATS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-4 md:grid-cols-4">
            {[
              { v: "2022", l: "Έτος ίδρυσης" },
              { v: "30+", l: "Μέλη ομάδας σε 9 χώρες" },
              { v: "200k+", l: "Επαγγελματίες στην Banana" },
              { v: "12M+", l: "Συνδέσεις που δημιουργήθηκαν" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-card p-8">
                <p className="font-display text-5xl leading-none">{s.v}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 md:grid-cols-2 md:px-6">
          <img src={storyImg} alt={storyAlt} className="w-full rounded-2xl object-cover" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Η ιστορία μας</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Από μια χάρτινη κάρτα στο συρτάρι, σε <em className="italic">έναν εγκέφαλο στην τσέπη σας.</em>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                Η Banana ιδρύθηκε το 2022, όταν η ομάδα μας διαπίστωσε ότι έχανε συνεχώς την επαφή με ανθρώπους που γνώριζε σε συνέδρια,
                συναντήσεις με επενδυτές και κλήσεις με πελάτες. Οι χάρτινες κάρτες κατέληγαν σε ένα συρτάρι. Το LinkedIn γέμιζε με
                ανθρώπους που δεν θυμόμασταν πού είχαμε γνωρίσει. Τα CRM μας ήταν άδεια.
              </p>
              <p>
                Ξεκινήσαμε με ένα προϊόν — μια κομψά σχεδιασμένη κάρτα NFC που άνοιγε ένα ψηφιακό προφίλ με
                ένα άγγιγμα. Ο κόσμος τη λάτρεψε. Η πραγματική μαγεία, όμως, ήρθε όταν προσθέσαμε AI: σημειώσεις συναντήσεων που γράφονταν
                μόνες τους, μηνύματα συνέχειας στο δικό σας ύφος και υπενθυμίσεις που ήξεραν πότε μια επαφή αποκτούσε ξανά
                ουσιαστική σημασία.
              </p>
              <p>
                Σήμερα, περισσότεροι από 200.000 επαγγελματίες χρησιμοποιούν την Banana — από ανεξάρτητους ιδρυτές έως εταιρικές ομάδες πωλήσεων —
                σε περισσότερες από 80 χώρες. Παραμένουμε μια μικρή ομάδα, εξακολουθούμε να στηριζόμαστε σε ίδια κεφάλαια και αναζητούμε επίμονα
                την απάντηση στο ίδιο ερώτημα: πώς βοηθάμε τους ανθρώπους να καλλιεργούν την ανθρώπινη πλευρά τους;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Η πορεία μας</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Τέσσερα χρόνια, <em className="italic">αμέτρητες κάρτες.</em>
          </h2>
          <ol className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              { y: "2022", t: "Ίδρυση", d: "Τρεις συνιδρυτές, μία πρωτότυπη κάρτα και ένα φύλλο Google με χρήστες δοκιμαστικής έκδοσης." },
              { y: "2023", t: "Οι πρώτοι 10.000 πελάτες", d: "Κυκλοφορήσαμε κάρτες από μέταλλο, μπαμπού και χρυσό. Το εργαλείο σημειώσεων AI κυκλοφόρησε το τέταρτο τρίμηνο." },
              { y: "2024", t: "Η εποχή του CRM", d: "Πλήρης διαχείριση ροών πωλήσεων, συμβάσεις, κρατήσεις και αυτοματισμοί — μία πλατφόρμα, μία τιμή." },
              { y: "2025", t: "Εταιρικές λύσεις και παγκόσμια παρουσία", d: "SOC 2 Type II, SAML SSO, αποθήκευση δεδομένων σε ΕΕ/ΗΠΑ/Ασία-Ειρηνικό. Περισσότεροι από 200.000 χρήστες." },
            ].map((m) => (
              <li key={m.y} className="rounded-2xl bg-card p-6">
                <p className="font-display text-3xl">{m.y}</p>
                <h3 className="mt-2 text-lg font-bold">{m.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Οι αρχές μας</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Όσα πιστεύουμε — <em className="italic">διατυπωμένα με πρόθεση.</em>
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              { i: Heart, t: "Πρώτα ο άνθρωπος. Πάντα.", d: "Το λογισμικό έχει αξία μόνο όταν ενισχύει την ανθρώπινη πλευρά μας. Δίνουμε προτεραιότητα στη συζήτηση, όχι στο κλικ." },
              { i: Sparkles, t: "Η AI ενισχύει, δεν αντικαθιστά.", d: "Χρησιμοποιούμε AI για να εξαλείφουμε τις δουλειές ρουτίνας, όχι για να προσποιούμαστε οικειότητα ή να παρακάμπτουμε το χτίσιμο σχέσεων. Η φωνή σας παραμένει δική σας." },
              { i: Shield, t: "Ιδιωτικότητα από προεπιλογή.", d: "Οι επαφές σας ανήκουν σε εσάς. Δεν εκπαιδεύουμε ποτέ μοντέλα με τα δεδομένα σας και δεν τα πουλάμε σε κανέναν. Εξαγάγετε ό,τι θέλετε, όποτε θέλετε." },
              { i: Eye, t: "Η αισθητική είναι η αφετηρία.", d: "Τα εργαλεία που χρησιμοποιείτε καθημερινά πρέπει να σας ευχαριστούν και οπτικά. Κάθε pixel, κάθε κάρτα, κάθε email — σχεδιασμένα με σκοπό." },
              { i: Zap, t: "Η ταχύτητα είναι πλεονέκτημα.", d: "Δίνουμε προσοχή σε κάθε καθυστέρηση, κάθε κίνηση και στον χρόνο έως το πρώτο άγγιγμα. Το πιο γρήγορο εργαλείο γίνεται καθημερινή συνήθεια." },
              { i: Globe, t: "Σχεδιασμένο για όλο τον κόσμο.", d: "Στέλνουμε κάρτες σε περισσότερες από 80 χώρες. Η AI μιλά περισσότερες από 50 γλώσσες. Τα δεδομένα αποθηκεύονται εκεί όπου ορίζει η νομοθεσία σας." },
            ].map((p) => {
              const Icon = p.i;
              return (
                <div key={p.t} className="rounded-2xl bg-muted p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-2xl">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Η ομάδα</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            30 άνθρωποι, <em className="italic">9 ζώνες ώρας, ένα κοινό πάθος.</em>
          </h2>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Είμαστε μια πλήρως απομακρυσμένη ομάδα σχεδιαστών, μηχανικών, ερευνητών AI και στελεχών λειτουργίας που έχουν δημιουργήσει λογισμικό
            στην Apple, τη Stripe, τη Notion, τη Loom και την Airbnb. Συναντιόμαστε από κοντά κάθε τρίμηνο — συνήθως κάπου με καλό φαγητό.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "Ανάπτυξη λογισμικού", c: "12 άτομα" },
              { n: "Σχεδιασμός", c: "5 άτομα" },
              { n: "AI και έρευνα", c: "4 άτομα" },
              { n: "Εμπορική ανάπτυξη", c: "9 άτομα" },
            ].map((d) => (
              <div key={d.n} className="rounded-2xl bg-card p-6">
                <div className="aspect-square w-full rounded-xl bg-muted" aria-hidden />
                <h3 className="mt-4 font-bold">{d.n}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{d.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTORS / PRESS */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Με τη στήριξη</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1]">Ανθρώπων με δοκιμασμένη εμπειρία.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Στηριχθήκαμε σε ίδια κεφάλαια έως το 2023 και στη συνέχεια συνεργαστήκαμε με έμπειρα στελέχη και ιδιώτες επενδυτές από την Apple, τη Stripe, τη Notion,
                τη Loom και την Airbnb. Αξιοποιούμε αποδοτικά τα κεφάλαιά μας, είμαστε κερδοφόροι και χτίζουμε με μακροπρόθεσμο ορίζοντα.
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Στον Τύπο</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1]">Ένα μικρό αποτύπωμα μέχρι σήμερα.</h2>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li>— «Η πιο όμορφη επαγγελματική κάρτα που είχα ποτέ.» <span className="text-foreground">Wired</span></li>
                <li>— «Το CRM της Banana είναι αυτό που θα έπρεπε να ήταν το Salesforce.» <span className="text-foreground">The Verge</span></li>
                <li>— «Η δικτύωση, επιτέλους, επαναπροσδιορίζεται.» <span className="text-foreground">Fast Company</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Θέλετε να διαμορφώσετε το μέλλον των σχέσεων <em className="italic text-brand-lime">μαζί μας;</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Αναζητούμε πάντα σχεδιαστές, μηχανικούς και στελέχη που αγαπούν την προσεγμένη δουλειά. Ή απλώς πείτε μας ένα γεια.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/teams" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Δείτε τις ανοιχτές θέσεις
              </Link>
              <Link to="/el/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Επικοινωνήστε μαζί μας
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
